import { FC, useState } from "react";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import {
    userRegistrationFirstStepFields,
    userRegistrationFirstStepSchemaFactory,
    userRegistrationSecondStepFields,
    userRegistrationSecondStepSchemaFactory,
} from "../../../validation-schemas/user-schemas/UserRegistrationSchema";
import { useTranslation } from "react-i18next";
import { RegistrationTabsEnum } from "../../../domain/enums/RegistrationTabsEnum";
import RegistrationFirstStepForm from "./RegistrationFirstStepForm";
import RegistrationSecondStepForm from "./RegistrationSecondStepForm";
import { useSelector } from "react-redux";
import { selectUser } from "../../../slices/user-slice";
import CustomTabulation from "../../../custom-components/CustomTabulation";
import { useErrorHandler } from "../../../custom-hooks/base-hooks/useErrorHandler";
import { StatusCodes } from "http-status-codes";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { UserServiceImpl } from "../../../services/UserServiceImpl";
import RegistrationSuccess from "./RegistrationSuccess";
import { useQueryParam } from "../../../custom-hooks/base-hooks/useQueryParam";

const RegistrationForm: FC = () => {
    const [tabName, setTabName] = useState(RegistrationTabsEnum.STEP1);
    const [isRegistrationCompleted, setIsRegistrationCompleted] = useState(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const user = useSelector(selectUser());
    const referralToken = useQueryParam("referralToken");

    const {
        form: firstStepForm,
        errorKey: firstStepErrorKey,
        setErrorKey: firstStepSetErrorKey,
    } = useCustomFormik({
        initial: null,
        fields: userRegistrationFirstStepFields,
        schema: userRegistrationFirstStepSchemaFactory(t),
        onSubmit: async (values) => {
            if (values.password !== values.confirmPassword) {
                firstStepSetErrorKey("errors.validation.passwordsDoNotMatch");
                return;
            }
            setTabName(RegistrationTabsEnum.STEP2);
        },
    });

    const {
        isError: emailAlreadyExist,
        resetErrors: resetConflictErrors,
        errorKey: conflictKey,
    } = useErrorHandler(StatusCodes.CONFLICT, "errors.response.custom.emailAlreadyExist");
    const {
        isError: invalidReferralToken,
        resetErrors: resetForbiddenErrors,
        errorKey: forbiddenKey,
    } = useErrorHandler(StatusCodes.FORBIDDEN, "errors.response.custom.invalidReferralToken");
    const {
        form: secondStepForm,
        errorKey: secondStepErrorKey,
        setErrorKey: secondStepSetErrorKey,
    } = useCustomFormik({
        initial: null,
        fields: userRegistrationSecondStepFields,
        schema: userRegistrationSecondStepSchemaFactory(t),
        onSubmit: async (values) => {
            resetConflictErrors();
            resetForbiddenErrors();
            const user = await dispatch(
                UserServiceImpl.registrationThunk({
                    ...values,
                    ...firstStepForm.values,
                    referralToken,
                })
            );

            if (user.meta.requestStatus !== "fulfilled") {
                secondStepSetErrorKey("errors.common");
                return;
            }
            if (emailAlreadyExist() || invalidReferralToken()) {
                return;
            }

            const result = await dispatch(UserServiceImpl.logoutThunk());
            if (result.meta.requestStatus === "fulfilled") {
                setIsRegistrationCompleted(true);
            }
        },
    });

    const isFirstStepValid =
        firstStepForm.isValid &&
        firstStepForm.values.password === firstStepForm.values.confirmPassword &&
        firstStepForm.dirty;
    const tabSwitchHandler = (tab: RegistrationTabsEnum) => {
        if (isFirstStepValid) {
            resetConflictErrors();
            resetForbiddenErrors();
            firstStepSetErrorKey("");
            secondStepSetErrorKey("");
            setTabName(tab);
        } else {
            firstStepSetErrorKey("errors.validation.fillInTheForm");
        }
    };

    return (
        <div
            className={`relative flex flex-col justify-center items-center ${
                user?.id ? "tmax4:h-[calc(100vh-108px)]" : "tmax4:h-[calc(100vh-68px)]"
            } mb-[80px]`}
        >
            <h1 className="H32B dmax2:H24B text-textWhite text-center my-[50px] td4:my-8 tmax4:my-6">
                {t("registration.registration")}
            </h1>
            <CustomTabulation
                tabsEnum={RegistrationTabsEnum}
                tabName={tabName}
                tabSwitchHandler={tabSwitchHandler}
                tabTitleKey="registration"
                wrapClassName="mb-5 dmax2:mb-3 text-center"
                unorderedListClassName="justify-center gap-5"
                listItemClassName="flex items-center"
                tabNameActiveClassName="H24B text-textWhite dmin2:px-5 pb-3 dmax2:pb-1 border-b-4 dmax2:border-b-3 border-textWarning"
                tabNameClassName="H20B text-textFaded dmin2:px-5 pb-5 dmax2:pb-2 border-textWarning"
            />
            <form
                id="registrationForm"
                onSubmit={firstStepForm.handleSubmit}
                className="relative w-[410px] dmax2:w-[340px]"
            >
                {tabName === RegistrationTabsEnum.STEP1 ? (
                    <RegistrationFirstStepForm form={firstStepForm} error={firstStepErrorKey} />
                ) : (
                    tabName === RegistrationTabsEnum.STEP2 && (
                        <RegistrationSecondStepForm
                            form={secondStepForm}
                            error={conflictKey || forbiddenKey || secondStepErrorKey || firstStepErrorKey}
                            foundUserEmail={emailAlreadyExist() && firstStepForm.values.email}
                        />
                    )
                )}
            </form>
            <RegistrationSuccess
                isRegistrationCompleted={isRegistrationCompleted}
                setIsRegistrationCompleted={setIsRegistrationCompleted}
            />
        </div>
    );
};

export default RegistrationForm;
